import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DownloadIcon from "@mui/icons-material/Download";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx-js-style";
import { getFestivalPaymentsOverview } from "../../services/festival";

export default function FestivalPayments() {
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [sortBy, setSortBy] = useState("last_payment_desc");
    const [page, setPage] = useState(1);
    const [festivals, setFestivals] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
    });
    const [kpis, setKpis] = useState({
        total_revenue: 0,
        total_platform_fees: 0,
        total_commissions: 0,
        total_net_settled: 0,
        total_active_festivals: 0,
        total_submissions: 0,
    });

    // Column Info Modal state
    const [colModal, setColModal] = useState({
        open: false,
        title: "",
        subtitle: "",
        bodyHtml: "",
        iconColor: "#0284c7",
        iconBg: "#e0f2fe",
    });

    // ─── Fetch Festival Payments Overview ─────────────────────────────────────
    const fetchOverview = useCallback(async () => {
        try {
            setLoading(true);
            const statusParam = status !== "all" ? status : "";
            const res = await getFestivalPaymentsOverview(search, page, statusParam, sortBy);
            if (res?.data) {
                if (res.data.festivals?.data) {
                    setFestivals(res.data.festivals.data);
                    setPagination({
                        current_page: res.data.festivals.current_page || 1,
                        last_page: res.data.festivals.last_page || 1,
                        per_page: res.data.festivals.per_page || 15,
                        total: res.data.festivals.total || 0,
                    });
                }
                if (res.data.kpis) {
                    setKpis(res.data.kpis);
                }
            }
        } catch (err) {
            console.error("Error loading festival payments overview:", err);
        } finally {
            setLoading(false);
        }
    }, [search, page, status, sortBy]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchOverview();
        }, 200);
        return () => clearTimeout(timer);
    }, [fetchOverview]);

    // ─── Helper for Festival Initials & Colors ────────────────────────────────
    const getInitials = (name) => {
        if (!name) return "FP";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getAvatarBg = (index) => {
        const colors = [
            { bg: "#fee4e2", text: "#f04438" },
            { bg: "#e0f2fe", text: "#0284c7" },
            { bg: "#dcfce7", text: "#16a34a" },
            { bg: "#f3e8ff", text: "#7c3aed" },
            { bg: "#fef3c7", text: "#d97706" },
        ];
        return colors[index % colors.length];
    };

    // ─── Column Explanations ──────────────────────────────────────────────────
    const columnExplanations = {
        gross_revenue: {
            title: "Gross Revenue",
            subtitle: "Total Submission Fees Collected",
            iconColor: "#0284c7",
            iconBg: "#e0f2fe",
            bodyHtml: "Total gross amount collected from all filmmaker submissions for this festival before any Freecomers Fee, gateway charges, or platform fees.",
        },
        platform_fee_col: {
            title: "Platform Fee (3%)",
            subtitle: "Filmmaker Checkout Surcharge",
            iconColor: "#0284c7",
            iconBg: "#e0f2fe",
            bodyHtml: "The global 3.0% platform fee paid directly by the filmmaker upon checkout. This is 100% Freecomers platform revenue and is never deducted from festival prize funds.",
        },
        commission_col: {
            title: "Freecomers Fee (6%)",
            subtitle: "Freecomers Retained Share",
            iconColor: "#16a34a",
            iconBg: "#dcfce7",
            bodyHtml: "The 6.0% Freecomers Fee retained from the festival's gross submission fee during multi-party settlement via Razorpay Route.",
        },
        net_settled: {
            title: "Net Paid to Festival",
            subtitle: "Direct Bank Account Settlement",
            iconColor: "#7c3aed",
            iconBg: "#f3e8ff",
            bodyHtml: "The actual net earnings transferred into the festival's verified bank account after Freecomers Fee (6%) and Razorpay Route settlement fee (0.25% + 18% GST).",
        },
    };

    const handleOpenColInfo = (key) => {
        const info = columnExplanations[key];
        if (info) {
            setColModal({
                open: true,
                ...info,
            });
        }
    };

    // ─── Local Sorting Helper ──────────────────────────────────────────────────
    const sortedFestivals = [...festivals].sort((a, b) => {
        const isAlpha = (str) => /^[a-zA-Z]/.test((str || "").trim());
        const nameA = (a.festival_name || "").trim();
        const nameB = (b.festival_name || "").trim();

        if (sortBy === "name_asc") {
            const aAlpha = isAlpha(nameA);
            const bAlpha = isAlpha(nameB);
            if (aAlpha && !bAlpha) return -1;
            if (!aAlpha && bAlpha) return 1;
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        }
        if (sortBy === "name_desc") {
            const aAlpha = isAlpha(nameA);
            const bAlpha = isAlpha(nameB);
            if (aAlpha && !bAlpha) return -1;
            if (!aAlpha && bAlpha) return 1;
            return nameB.localeCompare(nameA, undefined, { numeric: true, sensitivity: "base" });
        }
        if (sortBy === "last_payment_desc") {
            const dateA = a.last_payment_date ? new Date(a.last_payment_date).getTime() : 0;
            const dateB = b.last_payment_date ? new Date(b.last_payment_date).getTime() : 0;
            return dateB - dateA;
        }
        if (sortBy === "last_payment_asc") {
            const dateA = a.last_payment_date ? new Date(a.last_payment_date).getTime() : 0;
            const dateB = b.last_payment_date ? new Date(b.last_payment_date).getTime() : 0;
            return dateA - dateB;
        }
        if (sortBy === "revenue_desc") {
            return (Number(b.gross_revenue) || 0) - (Number(a.gross_revenue) || 0);
        }
        if (sortBy === "revenue_asc") {
            return (Number(a.gross_revenue) || 0) - (Number(b.gross_revenue) || 0);
        }
        return 0;
    });

    // ─── Export Overview Report ───────────────────────────────────────────────
    const handleExportReport = () => {
        if (!festivals || festivals.length === 0) {
            alert("No festival payment records available to export.");
            return;
        }

        try {
            const rows = sortedFestivals.map((f, idx) => ({
                "Sl. No": idx + 1,
                "Festival ID": f.film_festival_id || f.festival_id,
                "Festival Name": f.festival_name || "N/A",
                "Organiser": f.organiser || "Festival Organiser",
                "Bank Status": f.bank_status === "verified" ? `Verified (${f.bank_name || "Bank"})` : "Bank Pending",
                "Last Payment Date": f.last_payment_date_formatted || (f.last_payment_date ? new Date(f.last_payment_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"),
                "Gross Revenue (₹)": Number(f.gross_revenue || 0).toFixed(2),
                "Platform Fees (3%) (₹)": Number(f.platform_fees || 0).toFixed(2),
                "Freecomers Fee (6%) (₹)": Number(f.commissions || 0).toFixed(2),
                "Net Paid to Festival (₹)": Number(f.net_settled || 0).toFixed(2),
                "Status": f.status || "live",
            }));

            const ws = XLSX.utils.json_to_sheet(rows);

            // Auto-fit column widths
            const colWidths = Object.keys(rows[0] || {}).map(key => ({
                wch: Math.max(key.length, 14) + 3
            }));
            ws["!cols"] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Festival Payments");
            XLSX.writeFile(wb, `Festival_Payments_Overview_${new Date().toISOString().split("T")[0]}.xlsx`);
        } catch (err) {
            console.error("Export report error:", err);
            alert("Failed to export report.");
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3, mb: 10, px: { xs: 1.5, sm: 3 } }}>
            {/* ── Page Header ── */}
            <div style={{ marginBottom: "24px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 800, color: "#101828", margin: 0, marginBottom: "4px" }}>
                    Festival Payments Overview
                </h1>
                <div style={{ fontSize: "13px", color: "#667085" }}>
                    Track gross submission revenue, Freecomers platform fees, 6% Freecomers Fee deductions, and net payouts across all festivals.
                </div>
            </div>

            {/* ── 4 KPI Summary Cards (Grid) ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                    gap: "16px",
                    marginBottom: "24px",
                }}
            >
                {/* Card 1: Total Revenue */}
                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #eaecf0",
                        borderRadius: "14px",
                        padding: "18px 20px",
                        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                    }}
                >
                    <div style={{ fontSize: "11px", color: "#667085", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                        Total Revenue Collected
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: 800, color: "#101828", margin: "4px 0" }}>
                        ₹{Number(kpis.total_revenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: "11px", color: "#667085" }}>
                        Across {kpis.total_active_festivals || 0} Active Festivals
                    </div>
                </div>

                {/* Card 2: Platform Fee */}
                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #eaecf0",
                        borderRadius: "14px",
                        padding: "18px 20px",
                        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                    }}
                >
                    <div style={{ fontSize: "11px", color: "#0284c7", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                        Freecomers Platform Fee (3%)
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: 800, color: "#0284c7", margin: "4px 0" }}>
                        ₹{Number(kpis.total_platform_fees || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: "11px", color: "#667085" }}>
                        Filmmaker Platform Charge
                    </div>
                </div>

                {/* Card 3: Commission */}
                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #eaecf0",
                        borderRadius: "14px",
                        padding: "18px 20px",
                        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                    }}
                >
                    <div style={{ fontSize: "11px", color: "#16a34a", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                        Freecomers Fee (6%)
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: 800, color: "#16a34a", margin: "4px 0" }}>
                        ₹{Number(kpis.total_commissions || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: "11px", color: "#667085" }}>
                        Festival Share Cut
                    </div>
                </div>

                {/* Card 4: Net Paid */}
                <div
                    style={{
                        background: "#ffffff",
                        border: "1px solid #eaecf0",
                        borderRadius: "14px",
                        padding: "18px 20px",
                        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                    }}
                >
                    <div style={{ fontSize: "11px", color: "#7c3aed", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                        Net Paid to Festivals
                    </div>
                    <div style={{ fontSize: "22px", fontWeight: 800, color: "#7c3aed", margin: "4px 0" }}>
                        ₹{Number(kpis.total_net_settled || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontSize: "11px", color: "#667085" }}>
                        Direct Bank Payouts
                    </div>
                </div>
            </div>

            {/* ── Search and Filter Controls ── */}
            <div
                style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "stretch" : "center",
                    gap: "10px",
                    marginBottom: "16px",
                }}
            >
                {/* Search Bar */}
                <div
                    style={{
                        position: "relative",
                        flex: isMobile ? "none" : "1 1 260px",
                        maxWidth: isMobile ? "100%" : "340px",
                        width: isMobile ? "100%" : "auto",
                        minWidth: isMobile ? "100%" : "220px",
                    }}
                >
                    <SearchIcon
                        sx={{
                            position: "absolute",
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#98a2b3",
                            fontSize: 18,
                            pointerEvents: "none",
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search festival name or organiser..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        style={{
                            width: "100%",
                            height: "40px",
                            padding: "0 14px 0 38px",
                            border: "1px solid #d0d5dd",
                            borderRadius: "8px",
                            fontSize: "13.5px",
                            color: "#101828",
                            background: "#ffffff",
                            outline: "none",
                            boxSizing: "border-box",
                            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                            transition: "all 0.15s ease-in-out",
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = "#e90000";
                            e.target.style.boxShadow = "0 0 0 3px rgba(233, 0, 0, 0.1)";
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = "#d0d5dd";
                            e.target.style.boxShadow = "0 1px 2px rgba(16, 24, 40, 0.05)";
                        }}
                        onMouseEnter={(e) => {
                            if (document.activeElement !== e.target) e.target.style.borderColor = "#98a2b3";
                        }}
                        onMouseLeave={(e) => {
                            if (document.activeElement !== e.target) e.target.style.borderColor = "#d0d5dd";
                        }}
                    />
                </div>

                {/* Status Dropdown, Sort Dropdown & Export */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: "10px",
                        alignItems: isMobile ? "stretch" : "center",
                        width: isMobile ? "100%" : "auto",
                    }}
                >
                    {/* Status and Sort side-by-side in a 50/50 grid on mobile, flex row on desktop */}
                    <div
                        style={{
                            display: isMobile ? "grid" : "flex",
                            gridTemplateColumns: isMobile ? "1fr 1fr" : "none",
                            gap: "10px",
                            alignItems: "center",
                            width: isMobile ? "100%" : "auto",
                        }}
                    >
                        {/* Status Dropdown */}
                        <div style={{ position: "relative", display: "flex", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                            <FilterListIcon
                                sx={{
                                    position: "absolute",
                                    left: "11px",
                                    color: "#667085",
                                    fontSize: 17,
                                    pointerEvents: "none",
                                }}
                            />
                            <select
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setPage(1);
                                }}
                                style={{
                                    height: "40px",
                                    width: isMobile ? "100%" : "auto",
                                    minWidth: isMobile ? "0" : "150px",
                                    padding: isMobile ? "0 26px 0 32px" : "0 34px 0 34px",
                                    border: "1px solid #d0d5dd",
                                    borderRadius: "8px",
                                    fontSize: isMobile ? "12.5px" : "13px",
                                    fontWeight: 600,
                                    color: "#344054",
                                    background: "#ffffff",
                                    outline: "none",
                                    cursor: "pointer",
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23475467' stroke-width='1.66667' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: isMobile ? "right 10px center" : "right 12px center",
                                    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                                    boxSizing: "border-box",
                                    transition: "all 0.15s ease-in-out",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#e90000";
                                    e.target.style.boxShadow = "0 0 0 3px rgba(233, 0, 0, 0.1)";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#d0d5dd";
                                    e.target.style.boxShadow = "0 1px 2px rgba(16, 24, 40, 0.05)";
                                }}
                                onMouseEnter={(e) => {
                                    if (document.activeElement !== e.target) e.target.style.borderColor = "#98a2b3";
                                }}
                                onMouseLeave={(e) => {
                                    if (document.activeElement !== e.target) e.target.style.borderColor = "#d0d5dd";
                                }}
                            >
                                <option value="all">All Status</option>
                                <option value="verified">Verified Bank</option>
                                <option value="pending">Bank Pending</option>
                                <option value="live">Live / Open</option>
                            </select>
                        </div>

                        {/* Sort Dropdown */}
                        <div style={{ position: "relative", display: "flex", alignItems: "center", width: isMobile ? "100%" : "auto" }}>
                            <SwapVertIcon
                                sx={{
                                    position: "absolute",
                                    left: "11px",
                                    color: "#667085",
                                    fontSize: 18,
                                    pointerEvents: "none",
                                }}
                            />
                            <select
                                value={sortBy}
                                onChange={(e) => {
                                    setSortBy(e.target.value);
                                    setPage(1);
                                }}
                                style={{
                                    height: "40px",
                                    width: isMobile ? "100%" : "auto",
                                    minWidth: isMobile ? "0" : "165px",
                                    padding: isMobile ? "0 26px 0 32px" : "0 34px 0 34px",
                                    border: "1px solid #d0d5dd",
                                    borderRadius: "8px",
                                    fontSize: isMobile ? "12.5px" : "13px",
                                    fontWeight: 600,
                                    color: "#344054",
                                    background: "#ffffff",
                                    outline: "none",
                                    cursor: "pointer",
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    MozAppearance: "none",
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23475467' stroke-width='1.66667' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: isMobile ? "right 10px center" : "right 12px center",
                                    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                                    boxSizing: "border-box",
                                    transition: "all 0.15s ease-in-out",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#e90000";
                                    e.target.style.boxShadow = "0 0 0 3px rgba(233, 0, 0, 0.1)";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#d0d5dd";
                                    e.target.style.boxShadow = "0 1px 2px rgba(16, 24, 40, 0.05)";
                                }}
                                onMouseEnter={(e) => {
                                    if (document.activeElement !== e.target) e.target.style.borderColor = "#98a2b3";
                                }}
                                onMouseLeave={(e) => {
                                    if (document.activeElement !== e.target) e.target.style.borderColor = "#d0d5dd";
                                }}
                            >
                                <option value="last_payment_desc">Last Payment</option>
                                <option value="name_asc">Name (A – Z)</option>
                                <option value="name_desc">Name (Z – A)</option>
                                <option value="revenue_desc">Gross Revenue</option>
                            </select>
                        </div>
                    </div>

                    {/* Export Report Button */}
                    <button
                        type="button"
                        onClick={handleExportReport}
                        style={{
                            height: "40px",
                            width: isMobile ? "100%" : "auto",
                            background: "#ffffff",
                            color: "#344054",
                            border: "1px solid #d0d5dd",
                            padding: isMobile ? "0 14px" : "0 16px",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                            boxSizing: "border-box",
                            transition: "all 0.15s ease-in-out",
                            whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#98a2b3";
                            e.currentTarget.style.background = "#f9fafb";
                            e.currentTarget.style.boxShadow = "0 2px 4px rgba(16, 24, 40, 0.08)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#d0d5dd";
                            e.currentTarget.style.background = "#ffffff";
                            e.currentTarget.style.boxShadow = "0 1px 2px rgba(16, 24, 40, 0.05)";
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 16, color: "#475467" }} /> Export Report
                    </button>
                </div>
            </div>

            {/* ── Master Overview Table ── */}
            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #eaecf0",
                    borderRadius: "14px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                    maxWidth: "100%",
                }}
            >
                <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                    <table style={{ width: "100%", minWidth: "900px", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" }}>
                        <thead>
                            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #eaecf0" }}>
                                <th
                                    onClick={() => {
                                        setSortBy(prev => prev === "name_asc" ? "name_desc" : "name_asc");
                                        setPage(1);
                                    }}
                                    style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467", cursor: "pointer", userSelect: "none" }}
                                    title="Click to sort by Festival Name"
                                >
                                    Festival Name {sortBy === "name_asc" ? "↑" : sortBy === "name_desc" ? "↓" : "↕"}
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>Organiser</th>
                                <th
                                    onClick={() => {
                                        setSortBy(prev => prev === "last_payment_desc" ? "last_payment_asc" : "last_payment_desc");
                                        setPage(1);
                                    }}
                                    style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467", cursor: "pointer", userSelect: "none" }}
                                    title="Click to sort by Last Payment Date"
                                >
                                    Last Payment {sortBy === "last_payment_desc" ? "↓" : sortBy === "last_payment_asc" ? "↑" : "↕"}
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                    Gross Revenue{" "}
                                    <span
                                        onClick={() => handleOpenColInfo("gross_revenue")}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            background: "#e2e8f0",
                                            color: "#475467",
                                            fontSize: "9px",
                                            cursor: "pointer",
                                            marginLeft: "4px",
                                        }}
                                    >
                                        i
                                    </span>
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                    Platform Fee (3%){" "}
                                    <span
                                        onClick={() => handleOpenColInfo("platform_fee_col")}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            background: "#e2e8f0",
                                            color: "#475467",
                                            fontSize: "9px",
                                            cursor: "pointer",
                                            marginLeft: "4px",
                                        }}
                                    >
                                        i
                                    </span>
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                    Freecomers Fee (6%){" "}
                                    <span
                                        onClick={() => handleOpenColInfo("commission_col")}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            background: "#e2e8f0",
                                            color: "#475467",
                                            fontSize: "9px",
                                            cursor: "pointer",
                                            marginLeft: "4px",
                                        }}
                                    >
                                        i
                                    </span>
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                    Net Paid to Festival{" "}
                                    <span
                                        onClick={() => handleOpenColInfo("net_settled")}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            background: "#e2e8f0",
                                            color: "#475467",
                                            fontSize: "9px",
                                            cursor: "pointer",
                                            marginLeft: "4px",
                                        }}
                                    >
                                        i
                                    </span>
                                </th>
                                <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467", textAlign: "right" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} style={{ padding: "40px", textAlign: "center" }}>
                                        <CircularProgress size={30} sx={{ color: "#e90000" }} />
                                    </td>
                                </tr>
                            ) : festivals.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ padding: "40px", textAlign: "center", color: "#667085" }}>
                                        No film festivals found matching your search.
                                    </td>
                                </tr>
                            ) : (
                                sortedFestivals.map((fest, idx) => {
                                    const avatar = getAvatarBg(idx);
                                    const isBankVerified = fest.bank_status === "verified";
                                    return (
                                        <tr
                                            key={fest.film_festival_id}
                                            style={{
                                                borderBottom: "1px solid #f2f4f7",
                                                transition: "background-color 0.15s",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#faf8f5")}
                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                        >
                                            {/* Festival Name + Bank Badge */}
                                            <td style={{ padding: "14px 18px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <div
                                                        style={{
                                                            width: "36px",
                                                            height: "36px",
                                                            borderRadius: "50%",
                                                            background: avatar.bg,
                                                            color: avatar.text,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: 800,
                                                            fontSize: "12px",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(fest.festival_name)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: "#101828", fontSize: "13px" }}>
                                                            {fest.festival_name}
                                                        </div>
                                                        <div style={{ fontSize: "11px", marginTop: "3px" }}>
                                                            {isBankVerified ? (
                                                                <span
                                                                    style={{
                                                                        background: "#ecfdf5",
                                                                        color: "#027a48",
                                                                        padding: "2px 8px",
                                                                        borderRadius: "6px",
                                                                        fontWeight: 700,
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: "4px",
                                                                        fontSize: "10px",
                                                                    }}
                                                                >
                                                                    <AccountBalanceIcon sx={{ fontSize: 12 }} /> Verified {fest.bank_name || "Bank"}
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        background: "#fef3c7",
                                                                        color: "#b45309",
                                                                        padding: "2px 8px",
                                                                        borderRadius: "6px",
                                                                        fontWeight: 700,
                                                                        fontSize: "10px",
                                                                    }}
                                                                >
                                                                    ⚠️ Bank Pending
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Organiser */}
                                            <td style={{ padding: "14px 18px", color: "#344054", fontWeight: 500 }}>
                                                {fest.organiser || "Festival Organiser"}
                                            </td>

                                            {/* Last Payment (Date Only) */}
                                            <td style={{ padding: "14px 18px", color: fest.last_payment_date ? "#101828" : "#98a2b3", fontWeight: fest.last_payment_date ? 600 : 400, fontSize: "13px" }}>
                                                {fest.last_payment_date_formatted || (fest.last_payment_date ? new Date(fest.last_payment_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—")}
                                            </td>

                                            {/* Gross Revenue */}
                                            <td style={{ padding: "14px 18px", fontWeight: 700, color: "#101828" }}>
                                                ₹{Number(fest.gross_revenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* Platform Fee */}
                                            <td style={{ padding: "14px 18px", color: "#0284c7", fontWeight: 600 }}>
                                                +₹{Number(fest.platform_fees || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* Commission */}
                                            <td style={{ padding: "14px 18px", color: "#16a34a", fontWeight: 600 }}>
                                                +₹{Number(fest.commissions || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* Net Paid */}
                                            <td style={{ padding: "14px 18px", fontWeight: 800, color: "#7c3aed" }}>
                                                ₹{Number(fest.net_settled || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                            </td>

                                            {/* Action Button */}
                                            <td style={{ padding: "14px 18px", textAlign: "right" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/festivals/payments/${fest.film_festival_id}`)}
                                                    style={{
                                                        background: "#e90000",
                                                        color: "#ffffff",
                                                        border: "none",
                                                        padding: "7px 14px",
                                                        borderRadius: "8px",
                                                        fontSize: "12px",
                                                        fontWeight: 700,
                                                        cursor: "pointer",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        transition: "all 0.2s",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#c80000")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#e90000")}
                                                >
                                                    View Details <ArrowForwardIcon sx={{ fontSize: 14 }} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination footer */}
                {pagination.last_page > 1 && (
                    <div
                        style={{
                            padding: "14px 20px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px",
                            borderTop: "1px solid #eaecf0",
                            background: "#fcfcfd",
                        }}
                    >
                        <div style={{ fontSize: "12px", color: "#667085" }}>
                            Showing page <strong>{pagination.current_page}</strong> of <strong>{pagination.last_page}</strong> ({pagination.total} total festivals)
                        </div>
                        <Pagination
                            count={pagination.last_page}
                            page={pagination.current_page}
                            onChange={(e, v) => setPage(v)}
                            shape="rounded"
                            sx={{
                                "& .Mui-selected": {
                                    bgcolor: "#e90000 !important",
                                    color: "#fff !important",
                                },
                            }}
                        />
                    </div>
                )}
            </div>

            {/* ── Column Explanation Modal ── */}
            <Dialog
                open={colModal.open}
                onClose={() => setColModal((prev) => ({ ...prev, open: false }))}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                        maxWidth: 460,
                        width: "100%",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                            style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "10px",
                                background: colModal.iconBg,
                                color: colModal.iconColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 22 }} />
                        </div>
                        <div>
                            <Typography variant="subtitle1" fontWeight={800} color="#101828" sx={{ m: 0 }}>
                                {colModal.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {colModal.subtitle}
                            </Typography>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setColModal((prev) => ({ ...prev, open: false }))}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#98a2b3",
                            padding: "4px",
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </button>
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 3 }}>
                    <Typography variant="body2" color="#475467" sx={{ lineHeight: 1.6 }}>
                        {colModal.bodyHtml}
                    </Typography>
                </DialogContent>
            </Dialog>
        </Container>
    );
}
